import { EVENTS, publish } from '@sendible/event-manager';
import { reportErrorToFaro, shallowEqual } from '@sendible/common';

export * from './BridgeContext';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const COMPOSE_TABS = {
  MESSAGE: 'message',
  TWITTER: 'x',
  FACEBOOK: 'facebook',
  GOOGLE_MY_BUSINESS_LOCATION: 'googlemybusinesslocation',
  LINKEDIN: 'linkedin',
  PINTEREST: 'pinterest',
  TIKTOK: 'tiktok',
  INSTAGRAM: 'instagram',
  BLUESKY: 'bluesky',
  THREADS: 'threads',
  YOUTUBE: 'youtube',
  WORDPRESS: 'wordpress',
  GROUP: 'group',
  // NOTE: not a real network - a pseudo tab reused by the bulk importer so it can
  // drive the same media attach dropdown/upload pipeline as the compose box tabs.
  BULK_IMPORT: 'bulkImport',
} as const;

export type composeTabs = (typeof COMPOSE_TABS)[keyof typeof COMPOSE_TABS];

export interface CountInfo {
  taskCount?: number;
}
interface YouTube {
  tags: string;
  category: string;
  privacyStatus: string;
}

interface Thumbnail {
  url: string;
  offset: number;
}
interface VideoProperties {
  title: string;
  youtube?: YouTube;
  thumbnail?: Thumbnail;
}
interface VideoSettings {
  category: string;
  frameInMiliseconds: number;
  membersToNotify: string[];
  title: string;
  youtubePrivacyStatus: string;
  updatedMediaId?: string;
}
export interface IGUserTag {
  x: string;
  y: string;
  username: string;
  fileurl: string;
}

// these comes from messages API in message.photos
interface MessageMediaProperties {
  altText?: string;
  igUserTags?: IGUserTag[];
  videoFileSize?: number;
  videoSettings?: VideoSettings;
  videoProperties?: VideoProperties;
  fileDisplayName?: string;
}

export type PostType = 'post' | 'reel' | 'story';
export enum MediaFileType {
  Image = 'Image',
  Video = 'Video',
  Document = 'Document',
  Unspecified = 'Unspecified',
}
// these are properties that we need on the frontend only
type MessageMediaFrontendProperties = {
  id: MediaIdType;
  type: MediaFileType;
  orderNo: number;
  isEmbeddedImage?: boolean;
};

export type MessageMediaType = MessageMediaProperties & MessageMediaFrontendProperties;

export type TabState = {
  isCustomised?: boolean;
  postType: PostType;
  profileListServices?: composeTabs[];
  messageMedias?: {
    [key: MediaIdType]: MessageMediaType;
  };
};

export type Media = {
  url: string;
  type: 'image' | 'video' | 'pdf';
  id: MediaIdType | string;
  isGif?: boolean;
  name: string;
  downloadUrl: string;
  igUserTags?: IGUserTag[];
  fileDisplayName?: string;
};
export type MediaPreviewProps = {
  mediaList: Media[];
  initialMediaIndex: number;
};
export type TabsState = Partial<{
  [key in composeTabs]: TabState;
}>;

export type MediaIdType = number;

interface MediaImageMetadata {
  $type: string;
  height: number;
  contentType: string;
  size: number;
  width: number;
}

interface MediaVideoMetadata extends MediaImageMetadata {
  dimensions?: string;
  duration?: number;
  videoCodec?: string;
}
type ValidationCodesType =
  | 'DimensionsMustBeOneOf'
  | 'AspectRatioOutOfRange'
  | 'AspectRatioMustBeOneOf'
  | 'UnsupportedMediaType'
  | 'DurationOutOfRange'
  | 'MaxDurationOutOfRange'
  | 'RecommendedFacebookVideoDuration'
  | 'HeightOutOfRange'
  | 'MinHeightOutOfRange'
  | 'MaxHeightOutOfRange'
  | 'WidthOutOfRange'
  | 'MinWidthOutOfRange'
  | 'MaxWidthOutOfRange'
  | 'UnsupportedVideoCodec'
  | 'MaxVideoBitRateOutOfRange'
  | 'MinVideoBitRateOutOfRange'
  | 'FrameRateOutOfRange'
  | 'FrameRateMustBeOneOf'
  | 'MaxFrameRateOutOfRange'
  | 'SizeOutOfRange'
  | 'MaxSizeOutOfRange'
  | 'MaxPixelsOutOfRange'
  | 'MinDurationOutOfRange'
  | 'MinAudioBitRateOutOfRange'
  | 'UnsupportedAudioCodec'
  | 'AnimatedGifUnsupported'
  | 'DimensionsOutOfRange'
  | 'MaxThumbnailFileSizeOutOfRange';

type MediaSourceSets = {
  url: string;
  height: string;
  width: string;
};
type AspectRatioType = {
  width: number;
  height: number;
};
type DimensionType = {
  width: number;
  height: number;
};
type BoundedType = {
  min: number;
  max: number;
};
export type ValidationDetailType = {
  level: 'Required' | 'Recommendation';
  code: ValidationCodesType; // A unique error code
  allowed: {
    // describes the valid values the media can/must meet.
    values?: Array<{
      value?: string;
      aspectRatio?: AspectRatioType;
      dimensions?: DimensionType;
      bounded?: BoundedType;
    }>;
    value?: string;
    aspectRatio?: AspectRatioType;
    dimensions?: DimensionType;
    bounded?: BoundedType;
  };
};
type SingleNetworkValidationType = {
  post: Array<ValidationDetailType>;
  reel?: Array<ValidationDetailType>;
  story?: Array<ValidationDetailType>;
};

interface VideoSettings {
  category: string;
  frameInMiliseconds: number;
  membersToNotify: string[];
  title: string;
  youtubePrivacyStatus: string;
}
export interface MediaType {
  downloadUrl: string;
  thumbnailUrl: string;
  posterUrl?: string;
  originalUrl: string;
  metadata: MediaVideoMetadata;
  id: MediaIdType;
  name: string;
  failureReason?: string;
  type: MediaFileType;
  status: MediaStatusType;
  mediaLibraryId: number | null;
  createdById: number | null;
  createdAt: string;
  updatedById: number | null;
  updatedAt: string;
  deletedById: number | null;
  deletedAt: string | null;
  sourceSets: MediaSourceSets[];
  validations?: {
    [platform: string]: SingleNetworkValidationType;
  };
  components?: {
    poster?: {
      failureReason?: string;
      validations?: {
        [platform: string]: SingleNetworkValidationType;
      };
    };
  };
}

type MediaStatusType = 'New' | 'Processing' | 'Failed' | 'Ready' | 'Deleted';

export interface ComposeStore {
  activeComposeBoxTab: composeTabs;
  isComposeBoxOpen: boolean;
  isMediaAttacherOpen: boolean;
  isNewMediaSystem?: boolean;
  attachedTwMedia?: {
    [key: MediaIdType]: MediaType;
  };
  tabsState: TabsState;
}

export interface UserStore {
  accessToken: string;
  userId: number;
  originalUserId: number;
  username: string;
  email: string;
  language: string;
  timezone: number;
  featureTags: object;
  isPaused: boolean;
  isWhiteLabel: boolean;
  canNavigate: boolean;
  isSwitchedUser: boolean;
  isWorkspaceMode: boolean;
  countLabels?: CountInfo;
  weekStart?: 0 | 1;
  clockFormat?: 12 | 24;
}

export interface AppStore {
  addThreadsPromptOpenMode?: 'add' | 'reconnect' | null;
  addTikTokPromptOpenMode?: 'add' | 'reconnect' | null;
  addingServiceId: string | null;
  xConnectionModal: {
    isConnectOpen: boolean;
    accountId: null | number;
  };
  blueskyConnectionModal: {
    isConnectOpen: boolean;
    reconnectionAccountId: null | number;
  };
  mediaPreview: MediaPreviewProps & { isMediaPreviewOpen: boolean };
  // Set once the server reports the running dashboard version as unavailable. Consumed by
  // appRouter.js's nav click handler, which hard-reloads on the next safe main-nav navigation
  // instead of interrupting whatever the user is doing right now.
  dashboardReloadPending: boolean;
}

export interface SharedStateType {
  compose: ComposeStore;
  user: UserStore;
  app: AppStore;
}

export type BridgeContextType = [SharedStateType, React.Dispatch<React.SetStateAction<SharedStateType>>];
declare global {
  interface Window {
    sharedState: SharedStateType;
  }
}

function getSharedState() {
  return window.sharedState;
}

function setItem(value: SharedStateType) {
  window.sharedState = value;
  publish(EVENTS.SHARED_STATE_CHANGED, {});
}

function updateSpecificKey(store: keyof SharedStateType, key: string, value: unknown) {
  const newBridgeStateValue: SharedStateType = {
    ...window.sharedState,
  };

  if (!newBridgeStateValue[store]) {
    return;
  }

  try {
    const subStore = newBridgeStateValue[store] as unknown as Record<string, unknown>;

    // Skip writes that carry the value already held. Every publish re-renders each consumer of the
    // bridge, and callers routinely rewrite a key with a freshly built but identical object — the
    // legacy polling view rewrites user.countLabels every 45s with the same counts, and the compose
    // media poller rewrites attachedTwMedia once a second while an upload is in flight.
    if (shallowEqual(subStore[key], value)) {
      return;
    }

    subStore[key] = value;

    setItem(newBridgeStateValue);
  } catch (error: unknown) {
    reportErrorToFaro(error, 'sharedStateBridge updateSpecificKey');
  }
}

export { getSharedState, setItem, updateSpecificKey };
export default {
  getSharedState,
  setItem,
  updateSpecificKey,
};
